import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Pencil, FileText, Wrench, Palette, Check, Download, Package, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import EnquiryForm from "@/components/EnquiryForm";
import ExploreMore from "@/components/ExploreMore";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const defaultBenefits = [
  { icon: "Pencil", title: "Customisation Support", description: "We work closely with your design specifications, offering flexibility in materials, finishes, dimensions, and configurations to match your client's vision." },
  { icon: "FileText", title: "CAD Drawing Capability", description: "Our in-house design team provides detailed 2D/3D CAD drawings, elevations, and technical specifications for seamless project integration." },
  { icon: "Wrench", title: "Technical Coordination", description: "Dedicated technical team coordinates directly with your project managers for smooth execution, site measurements, and installation planning." },
  { icon: "Palette", title: "Sample Development", description: "Rapid prototyping and sample creation to help you and your clients visualize and approve designs before full-scale production." },
  { icon: "Download", title: "Downloadable CAD Blocks", description: "Access our library of AutoCAD blocks for workstations, cabins, and furniture layouts based on our experience centre mockups." },
  { icon: "Package", title: "White Labelling", description: "Offer our products under your brand. We provide complete white-label solutions with your branding on products and documentation." },
];

const defaultCadBlocks = [
  { name: "Linear Workstations (2-seater)", size: "120x60 cm" },
  { name: "Linear Workstations (4-seater)", size: "240x60 cm" },
  { name: "L-Shape Workstations", size: "150x150 cm" },
  { name: "Executive Cabin Layout", size: "Various" },
  { name: "Conference Table (8-seater)", size: "300x120 cm" },
  { name: "Reception Counter", size: "Various" },
];

const defaultContent = {
  heroTitle: "Architect & Consultant Partnership",
  heroSubtitle: "Partner Program",
  heroDescription: "We actively collaborate with architects and interior designers to deliver functional, customized workspace furniture solutions. Your design vision, our execution expertise.",
  heroImage: "https://images.unsplash.com/photo-1577561776790-1a20c419b1a2?auto=format&fit=crop&w=1280&q=70",
  ctaText: "Become a Partner",
  benefits: defaultBenefits,
  cadBlocks: defaultCadBlocks
};

const collaborationFeatures = [
  "Dedicated relationship manager for each project",
  "Priority production scheduling",
  "Bulk pricing benefits",
  "Joint site visits and client meetings",
  "Marketing co-branding opportunities",
  "Technical training for your team",
  "Exclusive previews of new products",
];

const iconMap = { Pencil, FileText, Wrench, Palette, Download, Package, Users };

export default function PartnersPage() {
  useCanonical("https://ezeefitofficesystems.in/partners");
  useMetaDescription("Partner with Ezeefit — modular office furniture manufacturer in Mumbai. Architect & consultant collaborations for corporate office interior fit-outs.");
  const [content, setContent] = useState(defaultContent);

  useEffect(() => {
    document.title = "Architect & Consultant Partnerships | Office Interior Furniture Solutions | Ezeefit Mumbai";
  }, []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/partners`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent({ ...defaultContent, ...response.data });
        }
      } catch (error) {
        console.log("Using default partners content");
      }
    };
    fetchContent();
  }, []);

  const getIcon = (iconName, index) => {
    return iconMap[iconName] || [Pencil, FileText, Wrench, Palette, Download, Package][index % 6];
  };

  const benefits = content.benefits || defaultBenefits;
  const cadBlocks = content.cadBlocks || defaultCadBlocks;

  return (
    <div className="pt-20" data-testid="partners-page">
      <Helmet>
        <meta name="description" content="Partner with Ezeefit — modular office furniture manufacturer in Mumbai. Architect & consultant collaborations for corporate office interior fit-outs." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/partners" />
      </Helmet>
      {/* Hero */}
      <section className="relative py-24 bg-[#0F172A] noise-overlay overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src={content.heroImage}
            alt="Architect and consultant partnership program for office furniture projects - Ezeefit Mumbai"
            className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              {content.heroSubtitle}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {content.heroTitle}
            </h1>
            <p className="text-xl text-[#94A3B8] max-w-2xl mb-8">
              {content.heroDescription}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                asChild
                className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
              >
                <Link to="/contact">
                  {content.ctaText || "Become a Partner"}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                className="bg-white/10 border border-white/30 text-white hover:bg-white hover:text-[#0F172A] rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
              >
                <a href="#cad-blocks">
                  Download CAD Blocks
                  <Download className="w-5 h-5 ml-2" />
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Partnership Benefits
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A]">
              Why Architects Choose Us
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = getIcon(benefit.icon, index);
              return (
                <motion.div
                  key={benefit.title || index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="p-8 bg-[#F8FAFC] border border-[#E2E8F0] card-hover"
                >
                  <div className="w-16 h-16 bg-[#0F172A] flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-[#F97316]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A] mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-[#64748B]">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CAD Blocks Download Section */}
      <section id="cad-blocks" className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Free Resources
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                Downloadable AutoCAD Blocks
              </h2>
              <p className="text-lg text-[#64748B] mb-8">
                Access our library of AutoCAD blocks for workstations and furniture layouts. 
                Based on our experience centre mockups, these blocks help you integrate 
                Ezeefit products seamlessly into your designs.
              </p>
              
              <div className="space-y-4 mb-8">
                {cadBlocks.map((block, index) => (
                  <div key={index} className="flex items-center justify-between p-4 bg-white border border-[#E2E8F0]">
                    <div>
                      <span className="font-medium text-[#0F172A]">{block.name}</span>
                      <span className="text-sm text-[#64748B] ml-2">({block.size})</span>
                    </div>
                    <Download className="w-5 h-5 text-[#F97316]" />
                  </div>
                ))}
              </div>

              <p className="text-sm text-[#64748B]">
                * Register as a partner to access the complete CAD library
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img src="https://images.unsplash.com/photo-1581674662583-5e89b374fae6?auto=format&fit=crop&w=1280&q=70"
                alt="CAD office furniture layout drawing - Ezeefit space planning and design support for architects"
                className="w-full h-auto" loading="lazy" decoding="async" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Collaboration Features */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Collaboration
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                What You Get as a Partner
              </h2>
              <p className="text-lg text-[#64748B] mb-8">
                Join our partner network of architects and interior designers who 
                rely on Ezeefit for their corporate furniture execution.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {collaborationFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-[#F97316] flex items-center justify-center shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-sm text-[#334155]">{feature}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-[#0F172A] p-8 text-white"
            >
              <h3 className="text-2xl font-bold mb-6">White Label Partnership</h3>
              <p className="text-[#94A3B8] mb-6">
                Offer premium office furniture under your brand. We handle manufacturing, 
                you focus on design and client relationships.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#F97316]" />
                  Your branding on products
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#F97316]" />
                  Custom documentation
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#F97316]" />
                  Private label warranty cards
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-[#F97316]" />
                  Confidential pricing
                </li>
              </ul>
              <Button
                asChild
                className="w-full bg-[#F97316] hover:bg-orange-600 text-white rounded-none py-4 font-semibold"
              >
                <Link to="/contact">Enquire About White Labelling</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partnership Form */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Get Started
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                Become a Partner
              </h2>
              <p className="text-lg text-[#64748B] mb-8">
                Fill out the form and our partnership team will reach out within 24 hours 
                to discuss how we can collaborate.
              </p>
              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F97316] flex items-center justify-center">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <span className="text-[#334155]">Submit your details</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F97316] flex items-center justify-center">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <span className="text-[#334155]">Connect with our partnership team</span>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F97316] flex items-center justify-center">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <span className="text-[#334155]">Access CAD blocks & start collaborating</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white p-8 border border-[#E2E8F0]"
            >
              <h3 className="text-xl font-bold text-[#0F172A] mb-6">
                Partnership Enquiry
              </h3>
              <EnquiryForm compact />
            </motion.div>
          </div>
        </div>
      </section>

      <ExploreMore currentPage="partners" />
    </div>
  );
}
